/**
 * FolderListHeader · 列表视图的表头行
 *
 * 与 FolderRow 列宽对齐 · 提供四列可点排序：
 *   · 文件夹名称 (folderName)
 *   · 照片数 (photoCount)
 *   · 子文件夹数 (subfolderCount)
 *   · 更新时间 (updateTime)
 *
 * 行为：
 *  · 点击当前 active 列 → asc / desc 切换
 *  · 点击其他列 → 切到该列，order 复位为 asc
 *  · 不支持取消排序（保持永远有排序，避免后端列表顺序混乱）
 *
 * 视觉：
 *  · 通栏一行，灰色文字 + sort 箭头；hover 浅蓝
 *  · 当前 active 列文字加深 + 蓝色箭头
 *  · 多选 / iconWrap 列保持空占位，只让带数据的列点击排序
 */

import React from 'react'
import classNames from 'classnames'
import {
  CaretDownOutlined,
  CaretUpOutlined,
  CheckOutlined
} from '@ant-design/icons'
import styles from './FolderListHeader.module.scss'

/** 与 uiSlice.FolderSortKey 同步 · 新增的 'ctime' 不会作为表头列展示
 *  （列表表头只展示 4 列可点排序），仅用于"当前外部排序态"的兜底渲染 */
export type FolderListSortKey =
  | 'folderName'
  | 'photoCount'
  | 'subfolderCount'
  | 'updateTime'
  | 'ctime'

export type FolderListSortOrder = 'asc' | 'desc'

export interface FolderListSort {
  key: FolderListSortKey
  order: FolderListSortOrder
}

export interface FolderListHeaderProps {
  sort: FolderListSort
  onSortChange: (next: FolderListSort) => void
  /** 多选模式 · 与 FolderRow 同步占位 selectIndicator 这一列的宽度 */
  selectMode?: boolean
  /** 多选模式下：表头第一列改为可点击的全选 checkbox（替代占位）*/
  allSelected?: boolean
  onToggleSelectAll?: () => void
  /** 多选模式下：在文件夹名称列后面显示 "已选择 N 项" 内联提示 */
  selectedCount?: number
}

interface ColumnDef {
  key: FolderListSortKey
  label: string
  /** 与 FolderRow.module.scss 的对应列保持一致 */
  className: string
  /** 默认升降序 · click 一个未 active 列时切到这个 order，符合"看了就想看大的/最近的" */
  defaultOrder: FolderListSortOrder
  /** 文字对齐 */
  align: 'left' | 'right'
}

const COLUMNS: ColumnDef[] = [
  {
    key: 'folderName',
    label: '文件夹名称',
    className: 'name',
    defaultOrder: 'asc',
    align: 'left'
  },
  {
    key: 'photoCount',
    label: '照片数',
    className: 'metaPhoto',
    defaultOrder: 'desc',
    align: 'right'
  },
  {
    key: 'subfolderCount',
    label: '子文件夹',
    className: 'metaSubfolder',
    defaultOrder: 'desc',
    align: 'right'
  },
  {
    key: 'ctime',
    label: '创建时间',
    className: 'metaCtime',
    defaultOrder: 'desc',
    align: 'right'
  },
  {
    key: 'updateTime',
    label: '更新时间',
    className: 'metaTime',
    defaultOrder: 'desc',
    align: 'right'
  }
]

const FolderListHeader: React.FC<FolderListHeaderProps> = ({
  sort,
  onSortChange,
  selectMode,
  allSelected,
  onToggleSelectAll,
  selectedCount
}) => {
  const handleClick = (col: ColumnDef): void => {
    if (sort.key === col.key) {
      onSortChange({
        key: col.key,
        order: sort.order === 'asc' ? 'desc' : 'asc'
      })
    } else {
      onSortChange({ key: col.key, order: col.defaultOrder })
    }
  }

  const renderSortIcon = (col: ColumnDef): React.ReactNode => {
    const active = sort.key === col.key
    if (!active) return <CaretDownOutlined className={styles.sortIconIdle} />
    return sort.order === 'asc' ? (
      <CaretUpOutlined className={styles.sortIconActive} />
    ) : (
      <CaretDownOutlined className={styles.sortIconActive} />
    )
  }

  return (
    <div
      className={styles.header}
      role="row"
      // BoxSelect 框选 mousedown 时遇到此元素跳过，避免点表头排序意外开启框选
      data-no-box-select
    >
      {/* 全选 checkbox · 表头第一列常驻 18px · 不再依赖 selectMode 显隐
          · 多选态：直接反映 allSelected 状态
          · 非多选态：点击触发 enterManageMode + 全选（由 onToggleSelectAll 实现）
          · 列宽与 FolderRow 第一列对齐 · 视觉与卡片 hover checkbox 一致 */}
      <span
        className={classNames(
          styles.selectIndicatorSlot,
          onToggleSelectAll && styles.selectIndicatorSlotClickable
        )}
        onClick={onToggleSelectAll}
        role={onToggleSelectAll ? 'checkbox' : undefined}
        aria-checked={!!allSelected}
        aria-label={allSelected ? '取消全选' : '全选'}
      >
        {onToggleSelectAll && (
          <span
            className={classNames(styles.selectIndicatorBox, {
              [styles.selectIndicatorBoxChecked]: allSelected
            })}
          >
            {allSelected && (
              <CheckOutlined style={{ fontSize: 12, color: '#fff' }} />
            )}
          </span>
        )}
      </span>
      <span className={styles.iconSlot} />
      {COLUMNS.map((col) => {
        const button = (
          <button
            type="button"
            className={classNames(
              styles.cell,
              // folderName 列特殊：button 不撑满，外层 div.name 才撑满（便于 "已选 N 项" 作为 sibling 放右侧）
              col.key !== 'folderName' && styles[col.className],
              sort.key === col.key && styles.cellActive,
              col.align === 'right' && styles.cellAlignRight
            )}
            onClick={() => handleClick(col)}
            aria-sort={
              sort.key === col.key
                ? sort.order === 'asc'
                  ? 'ascending'
                  : 'descending'
                : 'none'
            }
          >
            <span>{col.label}</span>
            {renderSortIcon(col)}
          </button>
        )
        // folderName 列：button 单独包一层 flex slot · "已选 N 项" 紧跟在排序三角后面
        // 不放在 button 内部 → 不影响 sort 点击区，也不挤压三角位置
        if (col.key === 'folderName') {
          return (
            <div key={col.key} className={styles.nameSlot}>
              {button}
              {selectMode &&
                typeof selectedCount === 'number' &&
                selectedCount > 0 && (
                  <span className={styles.selectedCountInline}>
                    · 已选择 {selectedCount} 项
                  </span>
                )}
            </div>
          )
        }
        return React.cloneElement(button, { key: col.key })
      })}
      {/* endSlot 占位 · 与 FolderRow 200px 对齐 */}
      <span className={styles.endSlot} />
    </div>
  )
}

export default FolderListHeader
